import { slick } from "@accessible360/accessible-slick";
import Swiper from "swiper";
import {
    Navigation,
    Thumbs,
    EffectFade,
    FreeMode,
    Keyboard,
} from "swiper/modules";

// import Swiper and modules styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/effect-fade";
import "swiper/css/a11y";
import "swiper/css/free-mode";
import "swiper/css/thumbs";
import "swiper/css/navigation";
import "swiper/css/keyboard";

class MediaGallery {
    constructor(element) {
        // Class properties.
        this.el = element;
        this.mainCarouselEl = this.el.querySelector("[data-main-carousel]");
        this.navCarouselEl = this.el.querySelector("[data-nav-carousel]");
        this.mainSwiper = null;
        this.navSwiper = null;

        this.removeVideoFromDialog = this.removeVideoFromDialog.bind(this);

        this.init();
    }

    init() {
        this.instantiateSwipers();
        this.handleVideoDialog();
    }

    instantiateSwipers() {
        // Instantiate the Nav carousel.
        if (this.navCarouselEl) {
            this.navSwiper = new Swiper(this.navCarouselEl, {
                loop: true,
                spaceBetween: "12",
                slidesPerView: 3,
                speed: 200,
                direction: "horizontal",
                freeMode: true,
                autoHeight: true,
                watchSlidesProgress: true,
                containerMessage: `Changing the current slide of this carousel will change
                the current slide of the preceding main image carousel`,
                containerRole: `region`,
                grabCursor: true,
                loopPreventsSliding: false,
                breakpoints: {
                    768: {
                        spaceBetween: "4.2%",
                    },
                },
                navigation: {
                    nextEl: ".thumbnail-button-next",
                    prevEl: ".thumbnail-button-prev",
                },
                modules: [Navigation, Thumbs, FreeMode],
            });
        }

        // Instantiate the main carousel.
        if (this.mainCarouselEl) {
            this.mainSwiper = new Swiper(this.mainCarouselEl, {
                loop: true,
                spaceBetween: 8,
                speed: 1000,
                autoHeight: true,
                effect: "fade",
                loopPreventsSliding: false,
                keyboard: {
                    enabled: true,
                    onlyInViewport: true,
                },
                fadeEffect: {
                    crossFade: true,
                },
                grabCursor: true,
                navigation: {
                    nextEl: ".swiper-button-next",
                    prevEl: ".swiper-button-prev",
                },
                thumbs: {
                    swiper: this.navSwiper,
                },
                containerRole: `region`,
                containerRoleDescriptionMessage: `Media gallery main carousel`,
                modules: [Navigation, Thumbs, EffectFade, Keyboard],
            });
        }
    }

    handleVideoDialog() {
        const videoButtons = this.el.querySelectorAll("[data-video-id]");
        if (!videoButtons) {
            return false;
        }

        videoButtons.forEach((button) => {
            const videoId = button.dataset.videoId;
            const dialogForId = this.el.querySelector(
                `[data-video-dialog="${videoId}"]`,
            );

            // Open dialog
            button.addEventListener("click", (event) => {
                this.addVideoToDialog(dialogForId);

                dialogForId?.showModal();
            });

            // Get dialog close btn
            const dialogCloseBtn = dialogForId.querySelector(
                "[data-dialog-close]",
            );

            // Close dialog (inside btn)
            dialogCloseBtn?.addEventListener("click", (event) => {
                this.closeDialog(dialogForId);
            });

            dialogForId?.addEventListener("click", (event) => {
                if (event.target.classList.contains("dialog-container")) {
                    this.closeDialog(dialogForId);
                }

                if (event.target.nodeName === "DIALOG") {
                    this.closeDialog(dialogForId);
                }
            });

            // Handle Escape key.
            if (dialogCloseBtn) {
                document.addEventListener("keyup", function (event) {
                    if (event.key === "Escape" || event.key === "Esc") {
                        this.closeDialog(dialogForId);
                    }
                });
            }
        });
    }

    closeDialog(dialog) {
        this.removeVideoFromDialog(dialog);

        dialog.close();
    }

    addVideoToDialog(dialogForId) {
        const videoIframe = this.getVideofromTemplate(dialogForId);
        const container = dialogForId.querySelector("div.content");

        if (container && videoIframe) {
            container.appendChild(videoIframe);
        }
    }

    removeVideoFromDialog(dialogForId) {
        const videoIframe = dialogForId.querySelector("iframe");
        if (videoIframe) {
            videoIframe.remove();
        }
    }

    getVideofromTemplate(dialogForId) {
        const template = dialogForId.querySelector("template");
        const templateContent = template?.content;

        if (templateContent) {
            // Clone the content (deep clone to include children)
            const clonedContent = document.importNode(templateContent, true);

            return clonedContent;
        } else {
            return false;
        }
    }
}

document.addEventListener("DOMContentLoaded", () => {
    const mediaCarousels = document.querySelectorAll(
        `.media:has([data-carousel="multiple"])`,
    );

    mediaCarousels.forEach((element) => {
        new MediaGallery(element);
    });
});

export default MediaGallery;
